import classnames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import BuyOnStoreButton from "web/react/components/buy-on-store-button/buy-on-store-button";
import Expandable from "web/react/components/expandable/expandable";
import PriceInfo from "web/react/components/price-info/price-info";
import SizePicker from "web/react/components/size-picker/size-picker";
import Shipping from "web/react/components/shipping/shipping";
import { gettext } from "web/script/modules/django-i18n";
import { ShippingOptionPropType, SizePropType } from "web/types/buy-option";
import styles from "./comparison-option.css";


function ComparisonOption({
    allSizesSorted,
    buyOptionSizes,
    className,
    shouldDisplaySizes,
    formattedPrice,
    freeReturn,
    isAlternativeOption,
    isInStock,
    isSavedForLater,
    leadUrl,
    onSale,
    productId,
    retailerName,
    salePrice,
    shippingOptions,
}) {
    /*
        in the main price area we need html from the backend that includes the
        correct itemprop metadata. for the price comparison we don't need this
    */
    const currentPriceHtml = onSale ? salePrice : formattedPrice;

    let selectedShipping;
    let freeShipping;
    let expressShipping;
    if (shippingOptions) {
        let { cheapest, cheapest_guaranteed: guaranteed, fastest } = shippingOptions;
        if (cheapest && cheapest.is_free) {
            freeShipping = cheapest;
        }
        if (guaranteed) {
            selectedShipping = guaranteed;
        } else {
            selectedShipping = cheapest;
        }
        expressShipping = fastest;
    }

    let shouldRenderShipping = freeShipping || expressShipping || freeReturn;

    className = classnames(className, styles.wrapper);

    return (
        <div className={className}>
            <div className={styles.content}>
                <div className={styles.left}>
                    <PriceInfo
                        currentPriceHtml={currentPriceHtml}
                        formattedPrice={formattedPrice}
                        isInStock={isInStock}
                        isOnSale={onSale}
                        isSavedForLater={isSavedForLater}
                        leadUrl={leadUrl}
                        retailerName={retailerName}
                        selectedShipping={selectedShipping}
                    />
                </div>
                <div className={styles.button}>
                    {isInStock ? (
                        <BuyOnStoreButton
                            isSavedForLater={isSavedForLater}
                            productId={productId}
                            leadUrl={leadUrl}
                            reason={
                                isAlternativeOption ? "buy-on-store-alternative-option" : undefined
                            }
                        />
                    ) : (
                        <span className={styles.oosText}>
                            {gettext("in_stock_product.compare_prices.out_of_stock_option") // "Out of stock"
                            }
                        </span>
                    )}
                </div>
            </div>
            {shouldDisplaySizes && allSizesSorted && (
                <div className={styles.subarea}>
                    <h3 className={styles.subtitle}>
                        {gettext("in_stock_product.sizes.title")}
                        {":"}
                    </h3>
                    <SizePicker buyOptionSizes={buyOptionSizes} allSizesSorted={allSizesSorted} />
                </div>
            )}
            {shouldRenderShipping && (
                <Expandable
                    className={styles.expandable}
                    renderLabel={() => (
                        <span className={styles.expandableLabel}>
                            {gettext("in_stock_product.compare_prices.all_shipping_returns.link")}
                        </span>
                    )}
                    chevronClass={styles.expandableChevron}
                >
                    <Shipping
                        expressShipping={expressShipping}
                        freeReturn={freeReturn}
                        freeShipping={freeShipping}
                        leadUrl={leadUrl}
                        retailerName={retailerName}
                    />
                </Expandable>
            )}
        </div>
    );
}

export default ComparisonOption;

ComparisonOption.propTypes = {
    allSizesSorted: PropTypes.array,
    buyOptionSizes: PropTypes.objectOf(SizePropType),
    className: PropTypes.string,
    shouldDisplaySizes: PropTypes.bool,
    formattedPrice: PropTypes.string.isRequired,
    freeReturn: PropTypes.bool.isRequired,
    isAlternativeOption: PropTypes.bool.isRequired,
    isInStock: PropTypes.bool.isRequired,
    isSavedForLater: PropTypes.bool.isRequired,
    leadUrl: PropTypes.string.isRequired,
    onSale: PropTypes.bool.isRequired,
    productId: PropTypes.string.isRequired,
    retailerName: PropTypes.string.isRequired,
    salePrice: PropTypes.string,
    shippingOptions: PropTypes.objectOf(ShippingOptionPropType),
};
